import { useState, useCallback } from 'react';
import { formatBytesAsSize } from '../utils/imageUtils';

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_FILES           = 20;

export const ACCEPTED_LABEL = '.jpg, .png, .webp, .gif';
export { MAX_FILES };

/**
 * Manages file selection, drag-and-drop, and per-file validation.
 *
 * @returns {{
 *   files: File[],
 *   errors: string[],
 *   optimizationType: string,
 *   isDragOver: boolean,
 *   setOptimizationType: Function,
 *   addFiles: Function,
 *   removeFile: Function,
 *   clearAll: Function,
 *   dropZoneProps: { onDragOver, onDragLeave, onDrop },
 *   handleFileInputChange: Function,
 * }}
 */
function useFileDropZone() {
  const [files, setFiles]               = useState([]);
  const [errors, setErrors]             = useState([]);
  const [optimizationType, setOptimizationType] = useState('regular');
  const [isDragOver, setIsDragOver]     = useState(false);

  const validateAndFilter = useCallback((fileList) => {
    const valid  = [];
    const errs   = [];
    Array.from(fileList).forEach((file) => {
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        errs.push(`${file.name}: unsupported type (accepted: ${ACCEPTED_LABEL})`);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        errs.push(`${file.name}: exceeds 20 MB limit (${formatBytesAsSize(file.size)})`);
      } else {
        valid.push(file);
      }
    });
    return { valid, errs };
  }, []);

  const addFiles = useCallback((fileList) => {
    const { valid, errs } = validateAndFilter(fileList);
    setErrors(errs);
    setFiles((prev) => {
      const existingNames = new Set(prev.map(f => f.name));
      const newOnes = valid.filter(f => !existingNames.has(f.name));
      const combined = [...prev, ...newOnes];
      if (combined.length > MAX_FILES) {
        setErrors(e => [...e, `Only the first ${MAX_FILES} files were added (maximum per session).`]);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  }, [validateAndFilter]);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearAll = useCallback(() => {
    setFiles([]);
    setErrors([]);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e, isIdle) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragOver(false);
    if (!isIdle) return;
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleFileInputChange = useCallback((e) => {
    addFiles(e.target.files);
    e.target.value = '';
  }, [addFiles]);

  const reset = useCallback(() => {
    setFiles([]);
    setErrors([]);
    setOptimizationType('regular');
    setIsDragOver(false);
  }, []);

  return {
    files, errors, optimizationType, isDragOver,
    setOptimizationType,
    addFiles, removeFile, clearAll, reset,
    handleDragOver, handleDragLeave, handleDrop, handleFileInputChange,
  };
}

export default useFileDropZone;
